// src/components/ArtAlign.tsx
import { useEffect } from 'react';

const SAMPLE_W = 320;
const EDGE_PERCENTILE = 97;
const MIN_PEAK_RATIO = 2.5;
const DEFAULT_RING_X = 0.5;
const DEFAULT_RING_Y = 0.447;
const SAFETY_SCALE = 1.02; 

export function parseArtUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const v = raw.trim();
  if (!v || v === 'none') return null;
  const m = v.match(/^url\(\s*(['"]?)([\s\S]*?)\1\s*\)$/i);
  if (m) return m[2].trim() || null;
  const bare = v.replace(/^['"]|['"]$/g, '').trim();
  return bare && bare !== 'none' ? bare : null;
}

export function ArtAlign() {
  useEffect(() => {
    let cancelled = false;
    let lastWritten = '';

    function rootFor(target: HTMLElement | null): HTMLElement | null {
      if (target) {
        let el: HTMLElement | null = target;
        while (el) {
          if (el.classList && el.classList.contains('art-bg')) return el;
          el = el.parentElement;
        }
      }
      return document.querySelector('.art-bg') as HTMLElement | null;
    }

    function findTarget(): HTMLElement | null {
      const all = Array.from(document.querySelectorAll<HTMLElement>('[data-art-center]'));
      for (const t of all) {
        const r = t.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) return t;
      }
      return all.length ? all[0] : null;
    }

    function artUrl(root: HTMLElement): string | null {
      return parseArtUrl(getComputedStyle(root).getPropertyValue('--art-image'));
    }

    function constantRing(root: HTMLElement): { x: number; y: number } {
      const cs = getComputedStyle(root);
      const fx = parseFloat(cs.getPropertyValue('--art-ring-x'));
      const fy = parseFloat(cs.getPropertyValue('--art-ring-y'));
      return {
        x: isFinite(fx) ? fx : DEFAULT_RING_X,
        y: isFinite(fy) ? fy : DEFAULT_RING_Y,
      };
    }

    function nudge(root: HTMLElement): { x: number; y: number } {
      const cs = getComputedStyle(root);
      const nx = parseFloat(cs.getPropertyValue('--art-nudge-x'));
      const ny = parseFloat(cs.getPropertyValue('--art-nudge-y'));
      return { x: isFinite(nx) ? nx : 0, y: isFinite(ny) ? ny : 0 };
    }

    function ensureArtImg(root: HTMLElement): HTMLImageElement {
      let artImg = root.querySelector(':scope > img.art-bg-img') as HTMLImageElement | null;
      if (!artImg) {
        artImg = document.createElement('img');
        artImg.className = 'art-bg-img';
        artImg.alt = '';
        artImg.decoding = 'async';
        root.insertBefore(artImg, root.firstChild);
      }
      return artImg;
    }

    function place(): boolean {
      if (cancelled) return false;

      const target = findTarget();
      if (!target) return false;
      const root = rootFor(target);
      if (!root) return false;

      const src = artUrl(root);
      if (!src) return false;

      if (!img || imgSrc !== src || !IMG_W || !IMG_H) {
        ensureImage(src);
        return false;
      }

      const t = target.getBoundingClientRect();
      if (!t.width || !t.height) return false;

      const box = root.getBoundingClientRect();
      const boxW = root.clientWidth || box.width;
      const boxH = root.clientHeight || box.height;
      if (!boxW || !boxH) return false;

      const c = constantRing(root);
      const cx = t.left + t.width / 2 - box.left;
      const cy = t.top + t.height / 2 - box.top;

      const coverScale = Math.max(boxW / IMG_W, boxH / IMG_H);
      const need = (n: number, d: number) => (d > 0 ? n / d : 0);

      let scale = coverScale;
      scale = Math.max(scale, need(cy, c.y * IMG_H));             
      scale = Math.max(scale, need(boxH - cy, (1 - c.y) * IMG_H)); 
      scale = Math.max(scale, need(cx, c.x * IMG_W));             
      scale = Math.max(scale, need(boxW - cx, (1 - c.x) * IMG_W)); 
      scale *= SAFETY_SCALE;

      const dispW = IMG_W * scale;
      const dispH = IMG_H * scale;

      const n = nudge(root);

      // Positive Y moves image UP. Positive X moves image RIGHT.
      const offsetX = cx - c.x * dispW + n.x;
      const offsetY = cy - c.y * dispH - n.y; 

      const artImg = ensureArtImg(root);
      if (artImg.getAttribute('src') !== src) {
        artImg.setAttribute('src', src);
      }

      const key = `${dispW.toFixed(2)}|${dispH.toFixed(2)}|${offsetX.toFixed(2)}|${offsetY.toFixed(2)}`;
      if (key !== lastWritten) {
        artImg.style.width = `${dispW}px`;
        artImg.style.height = `${dispH}px`;
        artImg.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
        lastWritten = key;
      }
      return true;
    }

    let IMG_W = 0;
    let IMG_H = 0;
    let imgSrc: string | null = null;
    let img: HTMLImageElement | null = null;

    function ensureImage(src: string) {
      if (imgSrc === src && img) return;
      imgSrc = src;
      IMG_W = 0;
      IMG_H = 0;
      const next = new Image();
      next.decoding = 'async';
      next.onload = () => {
        if (cancelled) return;
        img = next;
        IMG_W = next.naturalWidth;
        IMG_H = next.naturalHeight;
        lastWritten = '';
        place();
      };
      next.src = src;
    }

    const mo = new MutationObserver(() => place());
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] });

    const ro = new ResizeObserver(() => place());
    ro.observe(document.body);

    const themeObserver = new MutationObserver(() => {
      lastWritten = '';
      const target = findTarget();
      const root = rootFor(target);
      if (root) {
        const src = artUrl(root);
        if (src && (!img || imgSrc !== src)) ensureImage(src);
      }
      place();
    });
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'style'] });

    const onResize = () => place();
    window.addEventListener('resize', onResize);

    // Poller watches for changes in your CSS nudge variables
    let lastCssState = '';
    const cssPoller = setInterval(() => {
      const target = findTarget();
      const root = rootFor(target);
      if (root) {
        const n = nudge(root);
        const r = constantRing(root);
        const state = `${n.x},${n.y},${r.x},${r.y}`;
        if (state !== lastCssState) {
          lastCssState = state;
          console.log(`[ArtAlign] Knob changed! Nudge X: ${n.x}px, Nudge Y: ${n.y}px`);
          lastWritten = ''; // force update
          place();
        }
      }
    }, 250);

    place();

    return () => {
      cancelled = true;
      mo.disconnect();
      ro.disconnect();
      themeObserver.disconnect();
      clearInterval(cssPoller);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return null;
}

export default ArtAlign;